import React, { useState, useEffect } from 'react';
import { Input, Button, Typography, Progress, Card, message, Result } from 'antd';
import { LockOutlined, SafetyOutlined, CheckCircleOutlined, EyeOutlined, EyeInvisibleOutlined, KeyOutlined } from '@ant-design/icons';
import { Link, useHistory, useParams } from 'react-router-dom';
import auth from '../../../services/auth';
import { notify } from '../../../utils/helper';
import { ResetPasswordCard } from './Styled';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
const { Title, Text, Paragraph } = Typography;

// Animated Password Illustration component
const PasswordIllustration = () => (
  <div style={{ 
    height: '100%', 
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative'
  }}>
    <div style={{
      position: 'absolute',
      width: '120%',
      height: '120%',
      background: 'radial-gradient(circle at center, rgba(102, 0, 255, 0.3) 0%, rgba(51, 102, 255, 0.1) 50%, transparent 70%)',
      top: '-10%',
      left: '-10%',
      zIndex: 0
    }} />
    
    {/* Key Animation */}
    <div style={{
      position: 'relative',
      zIndex: 2,
      textAlign: 'center'
    }}>
      <div className="rotate-container" style={{
        width: 160,
        height: 160,
        background: 'rgba(51, 102, 255, 0.1)',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
        animation: 'glow 3s infinite alternate'
      }}>
        <div style={{
          width: 100,
          height: 100,
          background: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 30px rgba(51, 102, 255, 0.3)',
          animation: 'rotate 20s linear infinite'
        }}>
          <KeyOutlined style={{ 
            color: 'white', 
            fontSize: 48,
            animation: 'counterRotate 20s linear infinite'
          }} />
        </div>
      </div>
      
      <Title level={2} style={{ color: 'white', marginBottom: 16 }}>Reset Password</Title>
      <Paragraph style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: 16, maxWidth: 300 }}>
        Create a new strong password for your Meducate account
      </Paragraph>
    </div>
    
    {/* Security Elements */}
    <div className="floating-lock small" style={{
      position: 'absolute',
      width: 40,
      height: 40,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      top: '20%',
      left: '20%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 6s ease-in-out infinite 0.2s',
      zIndex: 1
    }}>
      <LockOutlined style={{ color: 'white', fontSize: 16 }} />
    </div>
    
    <div className="floating-lock medium" style={{
      position: 'absolute',
      width: 60,
      height: 60,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      bottom: '30%',
      right: '25%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 8s ease-in-out infinite 1s',
      zIndex: 1
    }}>
      <LockOutlined style={{ color: 'white', fontSize: 24 }} />
    </div>
    
    <div className="floating-shield" style={{
      position: 'absolute',
      width: 50,
      height: 50,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.1)',
      top: '60%',
      right: '15%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 7s ease-in-out infinite 0.5s',
      zIndex: 1
    }}>
      <SafetyOutlined style={{ color: 'white', fontSize: 20 }} />
    </div>
    
    {/* Animated Character Elements */}
    <div className="floating-char" style={{
      position: 'absolute',
      width: 40,
      height: 40,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.05)',
      bottom: '20%',
      left: '30%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 5s ease-in-out infinite 1.5s',
      zIndex: 1,
      fontSize: 16,
      color: 'white',
      fontWeight: 'bold'
    }}>
      A
    </div>
    
    <div className="floating-char" style={{
      position: 'absolute',
      width: 35,
      height: 35,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.05)',
      top: '40%',
      right: '20%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 6s ease-in-out infinite 0.8s',
      zIndex: 1,
      fontSize: 16,
      color: 'white',
      fontWeight: 'bold'
    }}>
      1
    </div>
    
    <div className="floating-char" style={{
      position: 'absolute',
      width: 30,
      height: 30,
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.05)',
      top: '25%',
      right: '35%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      animation: 'float 7s ease-in-out infinite 1.2s',
      zIndex: 1,
      fontSize: 16,
      color: 'white',
      fontWeight: 'bold'
    }}>
      #
    </div>
    
    {/* Bottom Wave */}
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      height: '25%',
      background: 'linear-gradient(to top, rgba(102, 0, 255, 0.2) 0%, transparent 100%)',
      clipPath: 'polygon(0 50%, 100% 0, 100% 100%, 0% 100%)',
      zIndex: 0
    }} />
  </div>
);

const PasswordStrengthIndicator = ({ value }) => {
  const getColorAndLabel = () => {
    if (value <= 25) return { color: '#ff4d4f', label: 'Weak' };
    if (value <= 50) return { color: '#faad14', label: 'Fair' };
    if (value <= 75) return { color: '#1890ff', label: 'Good' };
    return { color: '#52c41a', label: 'Strong' };
  };
  
  const { color, label } = getColorAndLabel();
  
  return (
    <>
      <Progress 
        percent={value} 
        showInfo={false} 
        strokeColor={color} 
        style={{ marginTop: 8 }} 
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>Password Strength</Text>
        <Text style={{ color, fontSize: 12, fontWeight: 500 }}>{label}</Text>
      </div>
    </>
  );
};

const PasswordRequirement = ({ met, text }) => (
  <div style={{ 
    display: 'flex', 
    alignItems: 'center', 
    marginBottom: 8,
    color: met ? '#52c41a' : 'rgba(0, 0, 0, 0.45)'
  }}>
    <div style={{ 
      width: 16, 
      height: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 8,
      borderRadius: '50%',
      background: met ? '#f6ffed' : 'rgba(0, 0, 0, 0.04)',
      border: `1px solid ${met ? '#b7eb8f' : 'rgba(0, 0, 0, 0.15)'}`,
    }}>
      {met ? <CheckCircleOutlined style={{ fontSize: 12, color: '#52c41a' }} /> : null}
    </div>
    <Text style={{ 
      fontSize: 14, 
      color: met ? 'rgba(0, 0, 0, 0.65)' : 'rgba(0, 0, 0, 0.45)',
      transition: 'color 0.3s'
    }}>
      {text}
    </Text>
  </div>
);

const ResetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formValues, setFormValues] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  // const [token, setToken] = useState('');
  const history = useHistory();
  const { token } = useParams();
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  // Password requirements checks
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    number: false,
    special: false
  });

  // Get token from URL
  // useEffect(() => {
  
  //   if (tokenFromUrl) {
  //     setToken(tokenFromUrl);
  //   } 
  // }, [history]);

  const checkPasswordStrength = (password) => {
    let strength = 0;
    let reqs = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password)
    };
    
    setRequirements(reqs);
    
    if (reqs.length) strength += 25;
    if (reqs.uppercase) strength += 25;
    if (reqs.number) strength += 25;
    if (reqs.special) strength += 25;
    
    return strength;
  };

  const handleChange = (field, value) => {
    setFormValues({
      ...formValues,
      [field]: value
    });
    
    if (field === 'password') {
      setPasswordStrength(checkPasswordStrength(value));
    }
    
    // Clear error when user types
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: null
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate password
    if (!formValues.password) {
      newErrors.password = 'Please enter a password';
    } else if (formValues.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength < 75) {
      newErrors.password = 'Password is not strong enough';
    }
    
    // Validate confirm password
    if (!formValues.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formValues.password !== formValues.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setLoading(true);
      try {
        // Execute reCAPTCHA verification
        if (!executeRecaptcha) {
          notify('error', 'reCAPTCHA not available. Please refresh the page and try again.');
          setLoading(false);
          return;
        }

        setRecaptchaLoading(true);
        let recaptchaToken;
        try {
          // Execute reCAPTCHA with 'reset_password' action
          recaptchaToken = await executeRecaptcha('reset_password');
          // console.log('reCAPTCHA token:', recaptchaToken);
          
          if (!recaptchaToken) {
            notify('error', 'reCAPTCHA verification failed. Please try again.');
            setLoading(false);
            setRecaptchaLoading(false);
            return;
          }
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          notify('error', 'reCAPTCHA verification failed. Please try again.');
          setLoading(false);
          setRecaptchaLoading(false);
          return;
        } finally {
          setRecaptchaLoading(false);
        }

        const response = await auth.resetPassword({
          token, 
          password: formValues.password,
          reCaptchaToken: recaptchaToken // Add reCAPTCHA token to payload
        });
        if(response.status === 200 || response.status === 201){
          notify('success', response?.data?.message || 'Password reset successfully!');
          setLoading(false);
          history.push('/login');
        }
      } catch (error) {
        notify('error', error.response?.data?.message || 'Failed to reset password. Please try again.');
        setLoading(false);
      }
    }
  };

  if (success) {
    return (
      <div style={{ 
        height: '100vh', 
        overflow: 'hidden',
        display: 'flex',
      }}>
        {/* Left side - Illustration (40%) */}
        <div style={{ 
          flex: '0 0 40%', 
          background: 'linear-gradient(135deg, #111122 0%, #1a1a40 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <PasswordIllustration />
        </div>
        
        {/* Right side - Success Message (60%) */}
        <div style={{ 
          flex: '0 0 60%', 
          background: 'white',
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px'
        }}>
          <div style={{ maxWidth: 450, width: '100%' }}>
            <Card
              bordered={false}
              style={{ 
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                borderRadius: 12,
                textAlign: 'center'
              }}
              bodyStyle={{ padding: 40 }}
            >
              <Result
                icon={
                  <div style={{
                    width: 80,
                    height: 80,
                    background: 'linear-gradient(135deg, #00c853 0%, #09b64e 100%)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    boxShadow: '0 8px 16px rgba(0, 200, 83, 0.2)',
                  }}>
                    <CheckCircleOutlined style={{ fontSize: 40, color: 'white' }} />
                  </div>
                }
                title="Password Reset Successful"
                subTitle="Your password has been changed successfully."
                extra={[
                  <Button 
                    type="primary" 
                    key="login" 
                    href="#login" 
                    size="large"
                    style={{
                      height: 50,
                      backgroundImage: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 16,
                      fontWeight: 500,
                      minWidth: 200,
                      marginTop: 16
                    }}
                  >
                    Back to Login
                  </Button>
                ]}
              />
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      height: '100vh', 
      overflow: 'hidden',
      display: 'flex',
    }}>
      {/* Left side - Illustration (40%) */}
      <div style={{ 
        flex: '0 0 40%', 
        background: 'linear-gradient(135deg, #111122 0%, #1a1a40 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <PasswordIllustration />
      </div>
      
      {/* Right side - Reset Form (60%) */}
      <div style={{ 
        flex: '0 0 60%', 
        background: 'white',
        overflowY: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: 450, width: '100%' }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <Title level={2} style={{ fontSize: 32, marginBottom: 8 }}>Create New Password</Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Your password must be different from previous used passwords
            </Text>
          </div>
          
          <ResetPasswordCard
            bordered={false}
            style={{ 
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              borderRadius: 12,
              padding: 20
            }}
          >
            <div style={{ marginBottom: 24 }}>
              <Text style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>New Password</Text>
              <Input.Password 
                prefix={<LockOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />}
                placeholder="Enter new password" 
                size="large"
                value={formValues.password}
                onChange={(e) => handleChange('password', e.target.value)}
                status={errors.password ? 'error' : ''}
                style={{ 
                  height: 50,
                  borderRadius: 8
                }}
                iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              />
              {errors.password ? (
                <Text type="danger" style={{ marginTop: 4, display: 'block' }}>{errors.password}</Text>
              ) : (
                formValues.password && <PasswordStrengthIndicator value={passwordStrength} />
              )}
            </div>

            <div style={{ marginBottom: 10 }}>
              <Text style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Confirm New Password</Text>
              <Input.Password 
                prefix={<LockOutlined style={{ color: 'rgba(0, 0, 0, 0.25)' }} />}
                placeholder="Confirm new password" 
                size="large"
                value={formValues.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                status={errors.confirmPassword ? 'error' : ''}
                style={{ 
                  height: 50,
                  borderRadius: 8
                }}
                iconRender={visible => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              />
              {errors.confirmPassword && (
                <Text type="danger" style={{ marginTop: 4, display: 'block' }}>{errors.confirmPassword}</Text>
              )}
            </div>

            <div style={{
              background: 'rgba(0, 0, 0, 0.02)',
              padding: 16,
              borderRadius: 8
            }}>
              <Text style={{ display: 'block', marginBottom: 12, fontWeight: 500 }}>
                <SafetyOutlined style={{ marginRight: 8 }} />
                Password Requirements
              </Text>
              
              <PasswordRequirement 
                met={requirements.length} 
                text="At least 8 characters"
              />
              <PasswordRequirement 
                met={requirements.uppercase} 
                text="At least one uppercase letter (A-Z)"
              />
              <PasswordRequirement 
                met={requirements.number} 
                text="At least one number (0-9)"
              />
              <PasswordRequirement 
                met={requirements.special} 
                text="At least one special character (!@#$%^&*)"
              />
            </div>

            <Button 
              type="primary" 
              size="large" 
              block
              loading={loading || recaptchaLoading}
              onClick={handleSubmit}
              style={{
                height: 50,
                backgroundImage: 'linear-gradient(135deg, #3366ff 0%, #6600ff 100%)',
                border: 'none',
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 500
              }}
              disabled={passwordStrength < 75}
            >
              {recaptchaLoading ? 'Verifying...' : 'Reset Password'}
            </Button>
            
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <Text type="secondary">
                Remember your password? <Link to="/login" style={{ color: '#3366ff', fontWeight: 500 }}>Sign In</Link>
              </Text>
            </div>
            
            {/* reCAPTCHA Badge */}
            <div className="recaptcha-badge">
              <div className="recaptcha-badge-icon">
                ✓
              </div>
              <span>Protected by reCAPTCHA</span>
            </div>
          </ResetPasswordCard>
        </div>
      </div>
      
      {/* CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes glow {
          0% { box-shadow: 0 0 10px rgba(51, 102, 255, 0.4); }
          100% { box-shadow: 0 0 30px rgba(51, 102, 255, 0.7); }
        }
        
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes counterRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(-360deg); }
        }

        .recaptcha-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 16px;
          padding: 8px 12px;
          background: rgba(0, 0, 0, 0.04);
          border-radius: 6px;
          font-size: 12px;
          color: rgba(0, 0, 0, 0.45);
        }

        .recaptcha-badge-icon {
          width: 16px;
          height: 16px;
          background: #52c41a;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 10px;
          font-weight: bold;
        }
      `}</style>
    </div>
  );
};

export default ResetPasswordPage;